import { useEffect, useMemo, useState } from "react";
import { CmSheetSectionProps } from "../../SchedaCM";
import { CmSheetFooter } from "../../Components/CmSheetFooter/CmSheetFooter";
import React from "react";
import { Divider } from "../../../../../../components/core/Divider/Divider";
import { IconManager } from "../../../../../../components/core/IconManager/IconManager";
import {
  Select,
  SelectOption,
  SelectVariantEnum,
} from "../../../../../../components/core/Select/Select";
import { ReactComponent as InfoIcon } from "../../../../../../icons/info.svg";
import {
  TitlesTyp,
  BodyTyp,
} from "../../../../../../components/Typography/Typography";
import { theme } from "../../../../../../constants/StyledComponents";
import { FontWeightEnum, IconSizeEnum } from "../../../../../../types/AppTypes";
import {
  StyledContainer,
  StyledGroup,
  StyledSubgroup,
  StyledSubgroupHeader,
  StyledIndicatorRow,
  StyledIndicatorValue,
  StyledCompletedCount,
  StyledForm,
  StyledMessageBox,
} from "./DevelopmentIndicators.styled";
import {
  ENACTED_BEHAVIOR_LABEL,
  EMPTY_DEVELOPMENT_INDICATORS_FOR_GRADE_MESSAGE,
  FILL_DEVELOPMENT_INDICATORS_MESSAGE_TOOLTIP,
  FULLY_ENACTED_LABEL,
  NOT_ENACTED_LABEL,
  OFTEN_ENACTED_LABEL,
  RARELY_ENACTED_LABEL,
  TOTAL_LABEL,
} from "../../../../../../constants/Dictionary";
import { Loader } from "../../../../../../components/core/Loader/Loader";
import { StyledHideOrShowContainer } from "../../SchedaCM.styled";
import { useAppSelector } from "../../../../../../hooks/store.hook";
import {
  useCmSheetDevelopmentIndicators,
  useUpdateCmSheetDevelopmentIndicators,
} from "../../../../../../hooks/cmSheet/useDevelopmentIndicators.hook";
import { useCmSheetInternalGrade } from "../../../../../../hooks/cmSheet/internalGrade.hook";
import { DevelopmentConfigItem } from "../../../../../../types/api/configurations.type";
import { UpdateCollaboratorCmSheetDevelopmentIndicatorsRequest } from "../../../../../../types/api/collaborators.type";
import { Controller, useForm } from "react-hook-form";
import {
  CmSheetStateEnum,
  PermissionsEnum,
} from "../../../../../../constants/AppConstants";
import { roleHasPermission } from "../../../../../../utils/roleAuth.utils";
import { EmptyState } from "../../../../../../components/EmptyState/EmptyState";
import developmentIndicatorsImage from "../../../../../../images/cm_development_indicators.png";

type IndicatorFormItem = {
  developmentId: number;
  developmentValue: number | null;
};

type FormData = {
  items: IndicatorFormItem[];
};

const evaluationValues: SelectOption<number>[] = [
  {
    label: NOT_ENACTED_LABEL,
    value: 0,
    description: "Comportamento non osservato",
  },
  {
    label: RARELY_ENACTED_LABEL,
    value: 1,
    description: "Comportamento osservato occasionalmente",
  },
  {
    label: OFTEN_ENACTED_LABEL,
    value: 2,
    description: "Emerge solo in poche occasioni",
  },
  {
    label: FULLY_ENACTED_LABEL,
    value: 3,
    description: "E' un comportamento consolidato e applicato sistematicamente",
  },
];

const evaluationOptions: SelectOption<number>[] = evaluationValues.map(
  (option) => ({
    ...option,
    label: `${option.value} - ${option.label}`,
  }),
);

export const DevelopmentIndicators = ({
  footer,
  cmSheetInfo,
  cmSheetStateMachineInfo,
  showFooter,
  isMyProfile,
  isInterim,
  edit,
  setEdit,
  setHeaderRightContent,
}: CmSheetSectionProps) => {
  const { config } = useAppSelector((state) => state.config);
  const { profile } = useAppSelector((state) => state.user);
  const [hasData, setHasData] = useState<boolean | null>(null);

  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { isDirty },
  } = useForm<FormData>();

  const selectedYearConfig = config.find(
    (item) => item.year === cmSheetInfo.configYear,
  );

  const { data, isFetching } = useCmSheetDevelopmentIndicators(cmSheetInfo.id);
  const { data: internalGradeData, isFetching: isFetchingInternalGrade } =
    useCmSheetInternalGrade(cmSheetInfo.id);

  const { updateDevelopmentsIndicators, isUpdating: updating } =
    useUpdateCmSheetDevelopmentIndicators(cmSheetInfo.id);

  const gradeId = internalGradeData?.start?.gradeId ?? null;

  const gradeDevelopments = useMemo(() => {
    if (gradeId === null || !selectedYearConfig) {
      return [];
    }

    const gradeConfig = selectedYearConfig.config.developments.find(
      (development) => development.gradeId === gradeId,
    );

    return [...(gradeConfig?.indicators ?? [])].sort(
      (a, b) => a.position - b.position,
    );
  }, [gradeId, selectedYearConfig]);

  useEffect(() => {
    if (setEdit) {
      setEdit(false);
    }
    setHasData(null);
  }, [cmSheetInfo]);

  useEffect(() => {
    if (isFetching || isFetchingInternalGrade || !data) return;

    if (selectedYearConfig) {
      reset({
        items: gradeDevelopments.map((development) => ({
          developmentId: development.id,
          developmentValue:
            data.indicators.find(
              (indicator) => indicator.developmentId === development.id,
            )?.developmentValue ?? evaluationValues[0].value,
        })),
      });
    }

    if (hasData === null) {
      setHasData(data.indicators.length > 0);
    }
  }, [
    data,
    gradeDevelopments,
    hasData,
    isFetching,
    isFetchingInternalGrade,
    reset,
    edit,
    cmSheetStateMachineInfo,
    selectedYearConfig,
  ]);

  const CompletedActivitiesCount = ({ count }: { count: number }) => (
    <StyledCompletedCount>
      <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="2">
        {TOTAL_LABEL}
      </BodyTyp>
      <BodyTyp
        fontWeight={FontWeightEnum.SEMIBOLD}
        variant="2"
        color={count > 0 ? "#7B5EED" : undefined}
      >
        {count}
      </BodyTyp>
    </StyledCompletedCount>
  );

  const watchedItems = watch("items") ?? [];
  const completedCount = watchedItems.reduce((acc, item) => {
    return acc + (item.developmentValue ?? 0);
  }, 0);

  const canEdit = () => {
    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(
          profile,
          PermissionsEnum.EDIT_DEVELOPMENT_INDICATORS_CM_SHEET,
        ) &&
        cmSheetStateMachineInfo.currentState ===
          CmSheetStateEnum.INTERMEDIATE_EVALUATION
      ) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    footer(
      <CmSheetFooter
        form="developmentCMForm"
        disabledEditButton={
          !isDirty || updating || isFetching || isFetchingInternalGrade
        }
        edit={edit}
        showEdit={canEdit() && gradeDevelopments.length > 0}
        setEdit={setEdit}
        cmSheetInfo={cmSheetInfo}
        cmSheetStateMachineInfo={cmSheetStateMachineInfo}
        isMyProfile={isMyProfile}
        showFooter={showFooter}
        isInterim={isInterim}
      />,
    );
  }, [
    isDirty,
    edit,
    hasData,
    isFetching,
    isFetchingInternalGrade,
    updating,
    gradeDevelopments.length,
    cmSheetInfo,
    cmSheetStateMachineInfo,
  ]);

  const onSubmit = async (data: FormData) => {
    try {
      const body: UpdateCollaboratorCmSheetDevelopmentIndicatorsRequest = [];
      data.items.forEach((i) => {
        body.push({
          developmentId: i.developmentId,
          developmentValue: i.developmentValue ?? null,
        });
      });
      await updateDevelopmentsIndicators(body);
    } finally {
      setHasData(null);
      setEdit(false);
    }
  };

  const getIndicatorValueLabel = (id: number): string => {
    const value =
      data?.indicators.find((i) => i.developmentId === id)?.developmentValue ??
      0;

    const label =
      evaluationValues.find((option) => option.value === value)?.label ??
      evaluationValues[0].label;

    return `${value} - ${label}`;
  };

  const canViewIndicatorDescription = () => {
    if (isMyProfile) {
      return true;
    }

    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        cmSheetInfo.careerManagerId === profile.id
      ) {
        return true;
      }

      if (
        roleHasPermission(
          profile,
          PermissionsEnum.EDIT_DEVELOPMENT_INDICATORS_CM_SHEET,
        )
      ) {
        return true;
      }
    }

    return false;
  };

  const showMessageBox = () => {
    if (
      !isMyProfile &&
      cmSheetStateMachineInfo.currentState === CmSheetStateEnum.DRAFT
    ) {
      if (
        roleHasPermission(
          profile,
          PermissionsEnum.EDIT_DEVELOPMENT_INDICATORS_CM_SHEET,
        )
      ) {
        return true;
      }
    }

    return false;
  };

  const isLoading = isFetching || isFetchingInternalGrade;
  const showLoader = isLoading && hasData === null;
  const showEmptyState = !isLoading && gradeDevelopments.length === 0;
  const showPage =
    !showLoader &&
    !showEmptyState &&
    (canViewIndicatorDescription() || canEdit());

  const fieldIndexById = useMemo(
    () => Object.fromEntries(gradeDevelopments.map((d, i) => [d.id, i])),
    [gradeDevelopments],
  );

  const groupedDevelopments = useMemo(
    () =>
      gradeDevelopments.reduce<
        Record<string, Record<string, DevelopmentConfigItem[]>>
      >((acc, item) => {
        if (!acc[item.macroCriterionName]) acc[item.macroCriterionName] = {};
        if (!acc[item.macroCriterionName][item.microCriterionName]) {
          acc[item.macroCriterionName][item.microCriterionName] = [];
        }
        acc[item.macroCriterionName][item.microCriterionName].push(item);
        return acc;
      }, {}),
    [gradeDevelopments],
  );

  const canViewIndicatorValue = () => {
    if (canEdit()) {
      return true;
    }

    if (!isMyProfile) {
      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        cmSheetInfo.careerManagerId === profile.id &&
        [CmSheetStateEnum.INTERMEDIATE_EVALUATION].includes(
          cmSheetStateMachineInfo.currentState,
        )
      ) {
        return true;
      }

      if (
        [
          CmSheetStateEnum.FINAL_CALIBRATION,
          CmSheetStateEnum.FINAL_FEEDBACK,
          CmSheetStateEnum.ARCHIVED_SHEET,
          CmSheetStateEnum.ARCHIVED_SHEET_WITH_TERMINATION,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    if (!canViewIndicatorValue() || gradeDevelopments.length === 0) {
      setHeaderRightContent(null);
      return;
    }

    setHeaderRightContent(<CompletedActivitiesCount count={completedCount} />);
  }, [completedCount, setHeaderRightContent, gradeDevelopments.length]);

  return (
    <>
      {showLoader && <Loader loading={true} className="cm-sheet-loader" />}
      {showEmptyState && (
        <div className="cm-sheet-empty-state">
          <EmptyState
            label={EMPTY_DEVELOPMENT_INDICATORS_FOR_GRADE_MESSAGE}
            imageSrc={developmentIndicatorsImage}
          />
        </div>
      )}
      <StyledHideOrShowContainer $show={showPage}>
        {showMessageBox() && (
          <StyledMessageBox>
            <IconManager size={IconSizeEnum.SMALL} color={theme.colors.black}>
              <InfoIcon />
            </IconManager>
            <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
              {FILL_DEVELOPMENT_INDICATORS_MESSAGE_TOOLTIP}
            </BodyTyp>
          </StyledMessageBox>
        )}
        <Loader loading={updating}>
          <StyledContainer
            $hasFooter={showFooter}
            $hasMessageBox={showMessageBox()}
          >
            <StyledForm
              id="developmentCMForm"
              onSubmit={handleSubmit(onSubmit)}
            >
              {Object.entries(groupedDevelopments).map(
                ([groupName, subgroups]) => (
                  <StyledGroup key={groupName}>
                    <TitlesTyp variant="1" fontWeight={FontWeightEnum.SEMIBOLD}>
                      {groupName}
                    </TitlesTyp>
                    {Object.entries(subgroups).map(
                      ([subgroupName, items], subIndex, arr) => (
                        <React.Fragment key={subgroupName}>
                          <StyledSubgroup>
                            <StyledSubgroupHeader>
                              <TitlesTyp
                                fontWeight={FontWeightEnum.SEMIBOLD}
                                variant="3"
                                color={theme.colors.purple}
                              >
                                {subgroupName}
                              </TitlesTyp>
                              {canViewIndicatorValue() && (
                                <BodyTyp
                                  fontWeight={FontWeightEnum.SEMIBOLD}
                                  variant="2"
                                >
                                  {ENACTED_BEHAVIOR_LABEL}
                                </BodyTyp>
                              )}
                            </StyledSubgroupHeader>
                            {items.map((item, indicatorIndex) => {
                              return (
                                <StyledIndicatorRow
                                  key={item.id}
                                  $isEven={indicatorIndex % 2 === 0}
                                >
                                  <TitlesTyp
                                    fontWeight={FontWeightEnum.REGULAR}
                                    variant="3"
                                  >
                                    {item.description}
                                  </TitlesTyp>
                                  {canViewIndicatorValue() && (
                                    <>
                                      <Controller
                                        name={`items.${fieldIndexById[item.id]}.developmentId`}
                                        control={control}
                                        render={({ field }) => (
                                          <input type="hidden" {...field} />
                                        )}
                                      />
                                      <StyledIndicatorValue>
                                        {edit ? (
                                          <Controller
                                            name={`items.${fieldIndexById[item.id]}.developmentValue`}
                                            control={control}
                                            render={({ field }) => (
                                              <Select
                                                variant={
                                                  SelectVariantEnum.SINGLE_SELECT
                                                }
                                                label={ENACTED_BEHAVIOR_LABEL}
                                                options={evaluationOptions}
                                                {...field}
                                                optionsContainerFitContent
                                              />
                                            )}
                                          />
                                        ) : (
                                          <BodyTyp
                                            fontWeight={FontWeightEnum.REGULAR}
                                            variant="2"
                                          >
                                            {getIndicatorValueLabel(item.id)}
                                          </BodyTyp>
                                        )}
                                      </StyledIndicatorValue>
                                    </>
                                  )}
                                </StyledIndicatorRow>
                              );
                            })}
                          </StyledSubgroup>
                          {subIndex < arr.length - 1 && <Divider margin={0} />}
                        </React.Fragment>
                      ),
                    )}
                  </StyledGroup>
                ),
              )}
            </StyledForm>
          </StyledContainer>
        </Loader>
      </StyledHideOrShowContainer>
    </>
  );
};
